import { apiClient } from '@/shared/api';

import type {
  BrowseCategoriesParams,
  Category,
  CategoryDetail,
  CategoryListRequest,
  CategoryListResponse,
  CategorySummaryPage,
  CategoryTranslation,
  CategoryTreeNode,
  CategoryUpsertRequest,
  CatalogVersionDetail,
  CatalogVersionSummaryPage,
  ImportResult,
} from '../model/types';

const BASE = '/api/v1/catalog/categories';
const MASTER_BASE = '/api/v1/catalog/master/categories';

export const listCategories = async (
  body: CategoryListRequest = {},
): Promise<CategoryListResponse> =>
  (await apiClient.post<CategoryListResponse>(BASE, body)).data;

export const getCategory = async (categoryId: string): Promise<Category> =>
  (await apiClient.get<Category>(`${BASE}/${categoryId}`)).data;

/**
 * Every stored translation of a category (epic-catalog-004, adr-tenant-005, plan-tenant-004
 * Wave 2) — unlike `getCategory`, not filtered/fallen-back to one locale. Feeds the merchant-portal
 * card-language switcher (`CategoryFormDialog`/`useCardLanguageDraft`).
 */
export const getCategoryTranslations = async (categoryId: string): Promise<CategoryTranslation[]> =>
  (await apiClient.get<CategoryTranslation[]>(`${BASE}/${categoryId}/translations`)).data;

/**
 * Search master-catalog categories — epic-catalog-002, story-024.
 * POST so the filter (incl. exact UUID `ids`) rides in the body and is searched in OpenSearch.
 */
export const searchCategories = async (
  filter: BrowseCategoriesParams = {},
): Promise<CategorySummaryPage> =>
  (await apiClient.post<CategorySummaryPage>(`${BASE}/search`, filter)).data;

export const createCategory = async (
  body: CategoryUpsertRequest,
): Promise<CategoryDetail> => (await apiClient.post<CategoryDetail>(MASTER_BASE, body)).data;

export const getMasterCategory = async (categoryId: string): Promise<CategoryDetail> =>
  (await apiClient.get<CategoryDetail>(`${MASTER_BASE}/${categoryId}`)).data;

export const deleteCategory = async (categoryId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${categoryId}`);
};

export const upsertCategory = async (
  categoryId: string,
  body: CategoryUpsertRequest,
): Promise<ImportResult> => (await apiClient.put<ImportResult>(`${BASE}/${categoryId}`, body)).data;

/**
 * Full tenant category tree in one call (epic-catalog-005, user-story-catalog-011) — unpaginated
 * by design. `locale` is optional; omitted, the tenant's default catalog language is used.
 */
export const getCategoryTree = async (locale?: string): Promise<CategoryTreeNode[]> =>
  (
    await apiClient.get<CategoryTreeNode[]>(`${BASE}/tree`, {
      params: locale ? { locale } : undefined,
    })
  ).data;

// ── Version history (epic-catalog-005, user-story-merchant-portal-032) — list + detail only, no
//    restore (unlike products, restoring a category version isn't part of this contract). ──────

/** Category version history, paged, newest first. */
export const getCategoryVersions = async (
  categoryId: string,
  params: { page?: number; size?: number } = {},
): Promise<CatalogVersionSummaryPage> =>
  (await apiClient.get<CatalogVersionSummaryPage>(`${BASE}/${categoryId}/versions`, { params })).data;

/** Full diff of one category version. */
export const getCategoryVersion = async (
  categoryId: string,
  number: number,
): Promise<CatalogVersionDetail> =>
  (await apiClient.get<CatalogVersionDetail>(`${BASE}/${categoryId}/versions/${number}`)).data;
